import {
  DatasheetPaperFormat,
  DatasheetPaperOrientation,
  MARGIN_IN_INCHES,
  PAPER_FORMAT_CONFIG,
  printDatasheet,
  WEB_TO_PRINTER_RESOLUTION,
} from "@modules/datasheets/components/datasheet-printing-settings";
import * as React from "react";

export type DatasheetPrintControllerProps = {
  printableDatasheetFieldId: string;
  printableDatasheetHolderId: string;
}

export const useDatasheetPrintController = ({ printableDatasheetFieldId, printableDatasheetHolderId }: DatasheetPrintControllerProps) => {
  const [printingSettingsOpened, setPrintingSettingsOpened] = React.useState(false);

  const getFarthestElement = React.useCallback(() => {
    const parent = document.getElementById(printableDatasheetHolderId);
    
    if (!parent) {
      return;
    }

    const [item] = (
      Array.from(parent.querySelectorAll(`[data-printable-element='${printableDatasheetFieldId}']`)) as HTMLElement[]
    ).sort((a, b) => b.offsetLeft - a.offsetLeft);

    return item;

  }, [printableDatasheetHolderId, printableDatasheetFieldId]);

  const getInchesFromPixels = React.useCallback((px: number) => px / WEB_TO_PRINTER_RESOLUTION, []);

  const handlePrintingSettingsOpen = React.useCallback(() => {
    setPrintingSettingsOpened(true);
  }, []);

  const handlePrintingSettingsClose = React.useCallback(() => {
    setPrintingSettingsOpened(false);
  }, []);

  const handlePrint = React.useCallback((params?: { pixelsToSkip?: number }) => {
    printDatasheet({
      printableElement: document.getElementById(printableDatasheetHolderId) as HTMLDivElement,
      pixelsToSkip: params?.pixelsToSkip,
    });
  }, [printableDatasheetHolderId]);

  const getOrientedInnerWidthInches = React.useCallback(
    ({ orientation, format }: { format: DatasheetPaperFormat; orientation: DatasheetPaperOrientation }) => {
      return orientation === DatasheetPaperOrientation.portrait
        ? PAPER_FORMAT_CONFIG[format].widthInInches
        : PAPER_FORMAT_CONFIG[format].heightInInches;
    },
    []
  );

  const handlePrintWithPageSkip = React.useCallback(
    ({
      pagesToSkip,
      format,
      orientation,
    }: {
      pagesToSkip: number;
      format: DatasheetPaperFormat;
      orientation: DatasheetPaperOrientation;
    }) => {
      const inchesFromLeft = getOrientedInnerWidthInches({ format, orientation }) * pagesToSkip;
      const inchesToPixels = inchesFromLeft * WEB_TO_PRINTER_RESOLUTION;

      handlePrint({
        pixelsToSkip: inchesToPixels,
      });
    },
    [handlePrint, getOrientedInnerWidthInches]
  );

  const getFarthestElementPositions = React.useCallback(() => {
    const element = getFarthestElement();

    if (!element) {
      return {
        left: 0,
        top: 0,
      };
    }

    return {
      left: element.offsetLeft + element.getBoundingClientRect().width,
      top: element.offsetTop + element.getBoundingClientRect().height,
    };
  }, [getFarthestElement]);

  const calculatePaperCount = React.useCallback(
    ({ format, orientation }: { format: DatasheetPaperFormat; orientation: DatasheetPaperOrientation }) => {
      const defaultPaperCount = 0;
      return Math.ceil(
        (getInchesFromPixels(getFarthestElementPositions().left) / getOrientedInnerWidthInches({ format, orientation })) + defaultPaperCount
      );
    },
    [getInchesFromPixels, getFarthestElementPositions]
  );

  const elementsFitOnPaper = React.useCallback(() => {
    return (
      getInchesFromPixels(getFarthestElementPositions().left) <
      PAPER_FORMAT_CONFIG[DatasheetPaperFormat.A4].widthInInches - MARGIN_IN_INCHES * 2
    );
  }, [getInchesFromPixels, getFarthestElementPositions]);

  const handlePrintDatasheetElements = React.useCallback(() => {
    if (elementsFitOnPaper()) {
      handlePrint();
    } else {
      handlePrintingSettingsOpen();
    }
  }, [elementsFitOnPaper, handlePrint, handlePrintingSettingsOpen]);

  return {
    handlePrintDatasheetElements,
    handlePrintingSettingsOpen,
    handlePrintingSettingsClose,
    getOrientedInnerWidthInches,
    printingSettingsOpened,
    calculatePaperCount,
    handlePrintWithPageSkip,
  };
};
